import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgProgress } from '@ngx-progressbar/core';
import { ToastrService } from 'ngx-toastr';
import { EditableComponent } from '../../../../shared/components/interfaces/editable.component';
import { UIDialogService } from '../../../../shared/services/ui-dialog.service';
import { ProductService } from '../../../product/service/product.service';
import { AuthService } from '../../../security/service/auth.service';
import { AssetRoutingStates } from '../../asset-routing.module';
import { AssetHelper } from '../../asset.helper';
import { AssetService } from '../../service/asset.service';
import { AssetBaseComponent } from '../asset-base.component';
import { AssetUIMessages } from '../model/asset-ui.messages';
import { AssetModel } from '../model/asset.model';

@Component({
  selector: 'asset-edit',
  templateUrl:
    './console/app-js/modules/asset/components/edit/asset-edit.component.html'
})
export class AssetEditComponent extends AssetBaseComponent
  implements EditableComponent {
  private originalAssetModel: AssetModel;
  protected duplicateSerialNumber = false;
  protected invalidSerialNumber = false;
  protected invalidAssetTag = false;

  constructor(
    toastr: ToastrService,
    progressService: NgProgress,
    authService: AuthService,
    assetService: AssetService,
    productService: ProductService,
    dialogService: UIDialogService,
    private activatedRoute: ActivatedRoute,
    private router: Router
  ) {
    super(
      progressService,
      toastr,
      authService,
      assetService,
      productService,
      dialogService
    );

    this.assetId = this.activatedRoute.snapshot.params['id'];
    this.loadAsset();
  }

  /**
   * On Save button click, validates the serial number then saves asset if valid, else sends an error message to user.
   */
  onSave(): void {
    this.progressRef.start();
    this.validateUniqueSerialNumberPromise(
      this.assetModel.serialNumber,
      this.assetModel.siteId,
      this.assetId
    ).then(
      result => {
        this.saveAsset();
        this.duplicateSerialNumber = false;
      },
      reason => {
        this.duplicateSerialNumber = true;
        this.onAPIError(reason);
      }
    );
  }
  // validate asset tag input typed in number only.
  protected isInvalidTag(): boolean {
    this.invalidAssetTag = this.validAssetTag();
    return this.invalidAssetTag;
  }

  /* Validate Asset Location Characters*/

  validateAssetLocationCharacters(event: any): void {
    const key = String.fromCharCode(event.keyCode);
    if (!this.validAssetLocation(key)) {
      event.preventDefault();
    }
  }

  validateAssetLocationOnPaste(event: ClipboardEvent): void {
    var key = event.clipboardData.getData('text');
    if (this.validateAssetLocationOnCopyPaste(key)) {
      event.preventDefault();
    }
  }

  /**
   * On Cancel button, go back to asset view page.
   */
  onCancel(): void {
    this.router.navigate([
      `/console/${AssetRoutingStates.LIST.url}`,
      this.assetId,
      'view'
    ]);
  }

  /**
   * Verify is object has changed.
   * @returns {boolean}
   */
  isDirty(): boolean {
    return (
      JSON.stringify(this.originalAssetModel) !==
      JSON.stringify(this.assetModel)
    );
  }

  /**
   * Load all the Asset information, calls to different nested endpoints.
   */
  private loadAsset(): void {
    this.progressRef.start();
    AssetHelper.getAssetDetails(this.assetId, this.assetService).subscribe(
      (res: any) => {
        this.originalAssetModel = res;
        this.assetModel = { ...this.originalAssetModel };
        // this.setSelectedSite(this.assetModel.siteId);
        this.checkForStarbucks();
        this.invalidSerialNumber = this.validSerialNumber();
        if (this.invalidSerialNumber) {
          this.assetModel.serialNumber = '';
        }
        this.operations.loading = false;
        this.progressRef.complete();
        this.loadLinkedLinesOfService();
      },
      () => this.onAPIError(AssetUIMessages.LOAD_ASSET_ERROR)
    );
  }

  /**
   * Saves the asset model by calling the endpoint.
   */
  private saveAsset(): void {
    if (!this.assetModel.assetLocation) {
      this.assetModel.assetLocation = '';
    } else {
      this.assetModel.assetLocation = this.removeTrailingSpacesForAssetLocation(
        this.assetModel.assetLocation
      );
    }
    this.assetService.updateAsset(this.assetModel).subscribe(
      res => {
        if (res) {
          this.onAPISuccess(AssetUIMessages.EDIT_ASSET_SUCCESS);
          this.router.navigate([
            `/console/${AssetRoutingStates.LIST.url}`,
            this.assetId,
            'view'
          ]);
        } else {
          console.log(AssetUIMessages.EDIT_ASSET_ERROR);
          if (res.errorMessage) {
            this.onAPIError(res.errorMessage);
          }
        }
      },
      err => this.onAPIError(AssetUIMessages.EDIT_ASSET_ERROR)
    );
  }

  /**
   * Overrided function to apply special logic when Starbucks site is selected.
   */
  protected onSiteChange() {
    if (this.isStarbucksSiteSelected()) {
      this.assetModel.product = null;
      this.assetModel.productId = null;
    }
    this.invalidSerialNumber = this.validSerialNumber();
    if (this.invalidSerialNumber) {
      this.assetModel.serialNumber = '';
    }
  }

  /**
   * Overrided function to apply special logic when Product selected is changed.
   */
  protected onProductChange(): void {
    this.invalidSerialNumber = this.validSerialNumber();
    if (this.invalidSerialNumber) {
      this.assetModel.serialNumber = '';
    }
  }
}
